import React, {useState, useRef, useMemo} from 'react';

import {Button, ButtonWithPopOut} from '@newsela/angelou';
import {css} from 'emotion';
import {
  get,
  isFunction,
  isUndefined,
} from 'lodash-es';
import _uniqueId from 'lodash-es/uniqueId';
import PropTypes from 'prop-types';
import {useSelector, useDispatch} from 'react-redux';

import SkipToContent from 'components/SkipToContent';
import TrackedLinkContainer from 'components/TrackedLink/';
import {clearReadPageMode} from 'modulesV2/actions';
import {getReadPageContext} from 'modulesV2/selectors';
import withTrackEvent from 'static/three-oh/src/components/with/WithTrackEvent';
import {getUser, getUserProperties} from 'static/three-oh/src/modules/selectors/userSelectors';
import {ReactComponent as CloseSvg} from 'staticImages/icons/close.svg';
import {ReactComponent as NewWindowSvg} from 'staticImages/icons/New-Window.svg';
import {ReactComponent as TelevisionSvg} from 'staticImages/icons/Television.svg';
import useFocusOut from 'utils/useFocusOut';

import {listItemLinkStyle} from '../GlobalNav/DrawerContents/style';
import NavMenuList from '../NavMenuList';
import NavMenuListItem from '../NavMenuList/NavMenuListItem';
import {breakpointSwitch} from '../style';
import JoinLink from './JoinLink';
import Logo from './Logo';
import SignInLink from './SignInLink';
import {
  alertStyle,
  avatarStyle,
  buttonWithPopOutStyle,
  containerStyle,
  edStyle,
  firstRight,
  logoStyle,
  presentationStyle,
  presentationButtonStyle,
  secondRight,
  svgWrapperStyle,
  tvSvgStyle,
  viewModeMessageStyle,
  windowSvgStyle
} from './style.jsx';
import UserAvatar from './UserAvatar';
import UserAvatarPopOut from './UserAvatarPopOut';
import UserDetailsTestInfo from './UserDetailsTestInfo/';

const propTypes = {
  email: PropTypes.string,
  history: PropTypes.object,
  hideUtilityNav: PropTypes.bool,
  isAuthenticated: PropTypes.bool,
  isFreeUser: PropTypes.bool,
  isStudent: PropTypes.bool,
  isPresentation: PropTypes.bool,
  isViewingFromAssignment: PropTypes.bool,
  skipToId: PropTypes.string,
  userName: PropTypes.string,
};

const ButtonWithTrackEvent = withTrackEvent(Button);
const ButtonWithPopOutTrackEvent = withTrackEvent(ButtonWithPopOut);
const AnchorWithTrackEvent = withTrackEvent((props) => <a {...props}>{props.children}</a>);


export const UtilityNav = React.forwardRef((props, ref) => {
  UtilityNav.displayName = 'UtilityNav';
  const {
    skipToId,
    isPresentation,
    isViewingFromAssignment,
    hideUtilityNav,
  } = props;

  const userName = props.userName ? props.userName : useSelector((state) => state?.user?.name);
  const user = useSelector((state) => getUser(state));
  const email = props.email ? props.email : useSelector((state) => state?.user?.email);
  const isStudent = props.isStudent ? props.isStudent : useSelector((state) => getUserProperties(state).isStudent);
  const isFreeUser = props.isFreeUser ? props.isFreeUser : useSelector((state) => getUserProperties(state).isFreeUser);
  const isAuthenticated = props.isAuthenticated ? props.isAuthenticated : useSelector((state) => getUserProperties(state).isAuthenticated);
  const dispatch = useDispatch();
  const hideNavigation = isPresentation || isViewingFromAssignment;
  const readPageContext = useSelector(getReadPageContext) || {};
  const previewSettings = get(readPageContext, 'contentPreviewSettings', {});

  /**
   * to keep track of which panel is open we use indexes.
   * 0 is for the ed center pop out
   * 1 is for the user avatar
   * -1 means all popouts are closed.
   */
  const [openIndex, setOpenIndex] = useState(-1);

  const avatarButtonRef = useRef(null);
  const avatarPopOutRef = useRef(null);

  const [edCenterButtonId] = useState(_uniqueId('edCenter'));
  const edCenterPopOutRef = useRef(null);
  const edCenterButtonRef = useRef(null);

  const isCurrentlyOpen = useMemo(() => (openIndex >= 0), [openIndex]);


  const focusRef = useFocusOut(() => {
    setOpenIndex(-1);
  }, isCurrentlyOpen);


  const onEsc = (ref, buttonRef) => {
    if (
      ref && ref.current
      && ref.current.contains(document.activeElement)) {
      buttonRef.current.focus();
    }
  };

  const EducatorCenterContent = () => {
    return (
      <NavMenuList>
        <NavMenuListItem>
          <TrackedLinkContainer
            eventName="clicked-utility-community"
            eventProperties={{
              description: 'clicked community link in ed center pop out',
            }}
            href="https://community.newsela.com"
            rel="noopener noreferrer"
            target="_blank"
            className={css(listItemLinkStyle)}
            actionName="Navigate"
            streamProperties={{
              description: 'clicked community link in ed center pop out',
            }}
          >
            Community
            <div className={svgWrapperStyle}>
              <NewWindowSvg className={windowSvgStyle} role="img"/>
            </div>
          </TrackedLinkContainer>
        </NavMenuListItem>
        {
        isFreeUser ? null :
        <NavMenuListItem>
          <TrackedLinkContainer
            eventName="clicked-utility-pro-learning"
            eventProperties={{
              description: 'clicked professional learning link in ed center pop out',
            }}
            href="/educator-center/"
            rel="noopener noreferrer"
            target="_blank"
            className={css(listItemLinkStyle)}
            actionName="Navigate"
            streamProperties={{
              description: 'clicked professional learning link in ed center pop out',
            }}
          >
            Professional Learning
            <div className={svgWrapperStyle}>
              <NewWindowSvg className={windowSvgStyle} role="img"/>
            </div>
          </TrackedLinkContainer>
        </NavMenuListItem>
      }
        <NavMenuListItem>
          <TrackedLinkContainer
            eventName="clicked-utility-support"
            eventProperties={{
              description: 'clicked support link in ed center pop out',
            }}
            href="https://support.newsela.com/"
            rel="noopener noreferrer"
            target="_blank"
            className={css(listItemLinkStyle)}
            actionName="Navigate"
            streamProperties={{
              description: 'clicked support link in ed center pop out',
            }}
          >
            Support
            <div className={svgWrapperStyle}>
              <NewWindowSvg className={windowSvgStyle} role="img"/>
            </div>
          </TrackedLinkContainer>
        </NavMenuListItem>
      </NavMenuList>
    );
  };

  const CtaButtonIcon = previewSettings.ctaIcon || CloseSvg;
  const CtaButtonText = previewSettings.ctaText || 'Close';
  const showLogo = isUndefined(previewSettings.showLogo) ? true : previewSettings.showLogo;

  return !hideUtilityNav && (
    <div
      ref={ref}
      className={hideNavigation ? presentationStyle : ''}
    >
      <ul
        className={containerStyle}
        ref={focusRef}
      >
        <li>
          {
            showLogo &&
            <AnchorWithTrackEvent
              href="/content/browse/browse-home"
              className={logoStyle}
              data-qa-selector="logo"
              eventName="header-logo-Navigate"
              eventProperties={{
                description: 'Clicked the header logo',
              }}
              aria-label="Newsela"
              actionName="Navigate"
              streamProperties={{description: 'Clicked the header logo'}}
            >
              <Logo
                fullSizeMediaQuery={breakpointSwitch}
              />
            </AnchorWithTrackEvent>
          }
        </li>
        <li>
          <SkipToContent href={`#${skipToId}`}/>
        </li>
        {
          !isAuthenticated && !hideNavigation &&
          <>
            <li
              className={firstRight}
              data-qa-selector="join"
            >
              <JoinLink/>
            </li>
            <li
              className={secondRight}
              data-qa-selector="sign_in"
            >
              <SignInLink/>
            </li>
          </>
        }

        {

          isAuthenticated && !isStudent && !hideNavigation &&
          (
            <li
              className={edStyle}
              ref={edCenterPopOutRef}
            >
              <ButtonWithPopOutTrackEvent
                actionPrefix={openIndex === 0 ? 'Initiate' : 'Cancel'}
                actionName="Navigate"
                id={edCenterButtonId}
                legacyEventName="click-utility-ed-center"
                streamProperties={{description: 'Toggle Education Center dropdown.'}}
                buttonContents="Educator Center"
                popOutContents={EducatorCenterContent()}
                ref={edCenterButtonRef}
                onClickButton={() => {
                  if (openIndex === 0) {
                    setOpenIndex(-1);
                  } else {
                    setOpenIndex(0);
                  }
                }}
                isPopOutVisible={openIndex === 0}
                onEsc={() => {
                  onEsc(edCenterPopOutRef, edCenterButtonRef);
                  setOpenIndex(-1);
                }}
                onClickOff={(event) => {
                  // need to account for them clicking on the button
                  if (!edCenterButtonRef.current.contains(event.target)) {
                    setOpenIndex(-1);
                  }
                }}
                __cssFor={buttonWithPopOutStyle}
              />
            </li>
          )
        }
        {
          isAuthenticated && userName && !hideNavigation &&
            <li
              style={{
                position: 'relative',
              }}
              className={avatarStyle}
              ref={avatarPopOutRef}
            >
              <UserAvatarPopOut
                buttonContents={
                  <UserAvatar
                    userName={userName}
                    ariaProps={{
                      'aria-label': `${userName} - settings`
                    }}
                  />
                }
                isVisible={openIndex === 1}
                onClickButton={() => {
                  if (openIndex === 1) {
                    setOpenIndex(-1);
                  } else {
                    setOpenIndex(1);
                  }
                }}
                ref={avatarButtonRef}
                onEsc={() => {
                  onEsc(avatarPopOutRef, avatarButtonRef);
                  setOpenIndex(-1);
                }}
                userName={userName}
                email={email}
                onClickOff={(event) => {
                  // need to account for them clicking on the button
                  if (!avatarButtonRef.current.contains(event.target)) {
                    setOpenIndex(-1);
                  }
                }}
              />
            </li>
          }
        {
          !isPresentation && isViewingFromAssignment &&
            <li className={`${alertStyle} ${viewModeMessageStyle}`}>
              {isUndefined(previewSettings.headerContents) ? <span>You are viewing this content from an assignment</span>
                : previewSettings.headerContents}
            </li>
        }
        {
          (isPresentation || isViewingFromAssignment) &&
            <li
              className={secondRight}
            >
              {
                isPresentation && !isViewingFromAssignment &&
                  <ButtonWithTrackEvent
                    objectType="Content"
                    actionName="Read"
                    eventProperties={{presentationMode: false}}
                    trackView={false}
                    legacyEventName="Article-PresentationMode"
                    streamProperties={{description: 'Exit Presentation Mode.'}}
                    __classNameFor={{
                      root: presentationButtonStyle
                    }}
                    onClick={() => dispatch(clearReadPageMode())}
                    statusColor={Button.statusColor.black}
                    dataProps={{
                      'data-qa-selector': 'exit'
                    }}
                  >
                    <TelevisionSvg aria-hidden="true" className={tvSvgStyle}/>
                    Exit
                  </ButtonWithTrackEvent>
              }
              {
                isViewingFromAssignment && !isPresentation &&
                  <ButtonWithTrackEvent
                    objectType="Content"
                    actionPrefix="Cancel"
                    actionName="Read"
                    legacyEventName="Article-ViewMode"
                    streamProperties={{description: 'Exit Preview (View Mode).'}}
                    __classNameFor={{
                      root: presentationButtonStyle
                    }}
                    statusColor={Button.statusColor.black}
                    labelPosition="before"
                    onClick={() => (
                      isFunction(previewSettings.action) ? previewSettings.action() : window.close()
                    )}
                    secondary trackView
                  >
                    <CtaButtonIcon aria-hidden="true" className={tvSvgStyle}/>
                    {CtaButtonText}
                  </ButtonWithTrackEvent>
              }
            </li>
        }
      </ul>
      <UserDetailsTestInfo user={user}/>
    </div>
  );
});

UtilityNav.propTypes = propTypes;

export default UtilityNav;
